"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { IconArrowNarrowLeft, IconArrowNarrowRight } from "@tabler/icons-react";
import Image from "next/image";
import type { GoogleReview, GoogleReviewsPayload } from "@/lib/google-reviews";

type TranslateFn = (key: string, values?: Record<string, string | number | Date>) => string;

interface SectionProps {
  t: TranslateFn;
  title?: string;
  initialGoogleData?: GoogleReviewsPayload | null;
  onMetaUpdate?: (meta: {
    rating: number | null;
    count: number | null;
    googleMapsUrl?: string;
  }) => void;
}

const MAX_TESTIMONIALS = 5;  
const avatarGradients = [
  "from-amber-400 to-orange-500",
  "from-brand-red to-brand-red",
  "from-blue-500 to-indigo-600",
  "from-rose-400 to-pink-500",
  "from-sky-400 to-cyan-600",
  "from-brand-red to-brand-red",
];

const localTestimonials = [
  {
    initials: "AG",
    nameKey: "testimonials.items.0.name",
    roleKey: "testimonials.items.0.role",
    quoteKey: "testimonials.items.0.quote",
    color: "from-brand-red to-brand-red",
  },
  {
    initials: "JM",
    nameKey: "testimonials.items.1.name",
    roleKey: "testimonials.items.1.role",
    quoteKey: "testimonials.items.1.quote",
    color: "from-blue-400 to-blue-600",
  },
  {
    initials: "LR",
    nameKey: "testimonials.items.2.name",
    roleKey: "testimonials.items.2.role",
    quoteKey: "testimonials.items.2.quote",
    color: "from-purple-400 to-purple-600",
  },
];

type TestimonialCard = {
  name: string;
  meta?: string;
  quote?: string;
  rating: number;
  time?: number;
  profilePhotoUrl?: string;
  initials?: string;
  color?: string;
};

const clampStars = (rating: number) => Math.max(0, Math.min(5, Math.round(rating)));

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

const selectFeaturedReviews = (reviews: GoogleReview[], maxItems: number): GoogleReview[] => {
  const fiveStars = [...reviews]
    .filter((r) => r.rating === 5)
    .sort((a, b) => (b.time ?? 0) - (a.time ?? 0));
  const withText = fiveStars.filter((r) => Boolean(r.text?.trim()));
  if (withText.length >= maxItems) return withText.slice(0, maxItems);
  const withoutText = fiveStars.filter((r) => !r.text?.trim());
  return [...withText, ...withoutText].slice(0, maxItems);
};

export function TestimonialsSection({ t, title, initialGoogleData, onMetaUpdate }: SectionProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [googleData, setGoogleData] = useState<GoogleReviewsPayload | null>(initialGoogleData ?? null);
  const [isLoading, setIsLoading] = useState(!initialGoogleData);
  const [hasError, setHasError] = useState(false);
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (initialGoogleData) return;

    let isMounted = true;
    const fetchReviews = async () => {
      try {
        const response = await fetch("/api/google-reviews");
        if (!response.ok) {
          throw new Error("Google reviews request failed");
        }
        const data = (await response.json()) as GoogleReviewsPayload;
        if (isMounted) {
          setGoogleData(data);
          setHasError(false);
        }
      } catch {
        if (isMounted) {
          setHasError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const idleId = idleWindow.requestIdleCallback?.(fetchReviews) ?? null;
    const timeoutId = idleId ? null : window.setTimeout(fetchReviews, 800);
    return () => {
      isMounted = false;
      if (idleId !== null) {
        idleWindow.cancelIdleCallback?.(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [initialGoogleData]);

  const fallbackCards = useMemo<TestimonialCard[]>(
    () =>
      localTestimonials.map((item) => ({
        name: t(item.nameKey).split(" ")[0] + "...",
        meta: t(item.roleKey),
        quote: t(item.quoteKey),
        rating: 5,
        initials: item.initials,
        color: item.color,
      })),
    [t],
  );

  const googleCards = useMemo<TestimonialCard[]>(() => {
    if (!googleData?.reviews?.length) return [];
    return selectFeaturedReviews(googleData.reviews, MAX_TESTIMONIALS).map((review, index) => ({
      name: (review.author_name?.split(" ")[0] ?? t("testimonials.googleSource")) + "...",
      meta: review.relative_time_description || t("testimonials.googleSource"),
      quote: review.text?.trim(),
      rating: review.rating ?? 0,
      time: review.time,
      profilePhotoUrl: review.profile_photo_url,
      initials: review.author_name ? getInitials(review.author_name) : "G",
      color: avatarGradients[index % avatarGradients.length],
    }));
  }, [googleData, t]);

  const shouldShowSkeleton = isLoading && !googleData;
  const visibleTestimonials = useMemo<TestimonialCard[]>(
    () =>
      shouldShowSkeleton
        ? []
        : (googleCards.length ? googleCards : fallbackCards).slice(0, MAX_TESTIMONIALS),
    [fallbackCards, googleCards, shouldShowSkeleton],
  );

  // Ensure newly added testimonial cards become visible by triggering their animation
  useEffect(() => {
    const container = sectionRef.current;
    if (!container) return;
    const items = container.querySelectorAll<HTMLElement>(".scroll-animate");
    items.forEach((el) => el.classList.add("animate-in"));
  }, [visibleTestimonials.length]);

  const aggregateRating = googleData?.rating ?? null;
  const aggregateCount = googleData?.userRatingsTotal ?? null;
  const cardScrollStep = 320;

  useEffect(() => {
    if (!onMetaUpdate) return;
    onMetaUpdate({
      rating: aggregateRating,
      count: aggregateCount,
      googleMapsUrl: googleData?.googleMapsUrl,
    });
  }, [aggregateRating, aggregateCount, googleData?.googleMapsUrl, onMetaUpdate]);

  const checkScrollability = () => {
    const container = scrollRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  const handleScrollLeft = () => {
    const container = scrollRef.current;
    if (!container) return;
    const newScroll = Math.max(container.scrollLeft - cardScrollStep, 0);
    container.scrollTo({ left: newScroll, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    const container = scrollRef.current;
    if (!container) return;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const newScroll = Math.min(container.scrollLeft + cardScrollStep, maxScroll);
    container.scrollTo({ left: newScroll, behavior: "smooth" });
  };

  useEffect(() => {
    checkScrollability();
  }, [visibleTestimonials.length]);

  const toggleExpanded = (key: string) => {
    setExpandedCards((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <section
      ref={sectionRef}
      className="section-y px-3 sm:px-4 lg:px-8 bg-gray-50 dark:bg-gray-900/50"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="mb-4 sm:mb-6 text-gray-900 dark:text-white px-2 sm:px-4 break-words type-h2">
            {title ?? t("testimonials.title")}
          </h2>
          <div className="mt-4 sm:mt-6 flex flex-col items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            {shouldShowSkeleton ? (
              <>
                <div className="h-5 sm:h-6 w-24 sm:w-36 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-3 sm:h-4 w-48 sm:w-60 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </>
            ) : (
              <>
                {aggregateRating !== null && aggregateCount !== null && (
                  <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="text-lg sm:text-2xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
                        {aggregateRating.toFixed(1)}
                      </span>
                      <div className="flex">
                        {Array.from({ length: 5 }).map((_, index) => (
                          <svg
                            key={index}
                            className={`w-4 sm:w-5 h-4 sm:h-5 ${
                              index < clampStars(aggregateRating)
                                ? "text-yellow-400"
                                : "text-gray-300 dark:text-gray-700"
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                    <span>
                      {t("testimonials.googleSummary", {
                        rating: aggregateRating.toFixed(1),
                        count: aggregateCount,
                      })}
                    </span>
                  </div>
                )}
                {isLoading && <span>{t("testimonials.googleLoading")}</span>}
                {hasError && !isLoading && <span>{t("testimonials.googleUnavailable")}</span>}
              </>
            )}
          </div>
        </div>
        <div className="relative">
          <div
            ref={scrollRef}
            onScroll={checkScrollability}
            className="flex gap-3 sm:gap-4 lg:gap-6 overflow-x-auto scroll-smooth pb-4 sm:pb-6 [scrollbar-width:none] [-ms-overflow-style:none]"
          >
            {shouldShowSkeleton
              ? Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={`testimonial-skeleton-${index}`}
                    className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-md sm:shadow-lg min-w-[240px] sm:min-w-[280px] md:min-w-[300px] animate-pulse"
                  >
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 mr-3"></div>
                      <div className="space-y-2">
                        <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    </div>
                    <div className="flex mb-2 gap-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div
                          key={`star-skeleton-${index}-${i}`}
                          className="h-3 w-3 rounded bg-gray-200 dark:bg-gray-700"
                        ></div>
                      ))}
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                      <div className="h-3 w-5/6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  </div>
                ))
              : visibleTestimonials.map((item, index) => (
                  <div
                    key={`${item.name}-${index}`}
                    className="scroll-animate fade-in-up bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-md sm:shadow-lg min-w-[240px] sm:min-w-[280px] md:min-w-[300px]"
                    style={{ animationDelay: `${index * 0.08}s` }}
                  >
                    <div className="flex items-center mb-2 sm:mb-3 gap-2 sm:gap-3">
                      {item.profilePhotoUrl && !failedImages[item.profilePhotoUrl] ? (
                        <div className="w-9 sm:w-10 h-9 sm:h-10 relative flex-shrink-0">
                          <Image
                            src={item.profilePhotoUrl}
                            alt=""
                            width={40}
                            height={40}
                            className="rounded-full object-cover w-full h-full"
                            loading="lazy"
                            decoding="async"
                            referrerPolicy="no-referrer"
                            onError={() =>
                              setFailedImages((prev) => ({
                                ...prev,
                                [item.profilePhotoUrl ?? "unknown"]: true,
                              }))
                            }
                          />
                        </div>
                      ) : (
                        <div
                          className={`w-9 sm:w-10 h-9 sm:h-10 bg-gradient-to-br ${
                            item.color ?? avatarGradients[index % avatarGradients.length]
                          } rounded-full flex items-center justify-center text-white font-semibold text-sm sm:text-base flex-shrink-0`}
                        >
                          {item.initials ?? getInitials(item.name)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {item.name}
                        </p>
                        {item.meta && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{item.meta}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex mb-2 gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <svg
                          key={i}
                          className={`w-3.5 sm:w-4 h-3.5 sm:h-4 ${
                            i < clampStars(item.rating)
                              ? "text-yellow-400"
                              : "text-gray-300 dark:text-gray-700"
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    {item.quote && (
                      <div>
                        <p
                          className={`text-sm text-gray-700 dark:text-gray-300 leading-relaxed ${
                            expandedCards[`${item.name}-${index}`] ? "" : "line-clamp-2 sm:line-clamp-3"
                          }`}
                        >
                          “{item.quote}”
                        </p>
                        {item.quote.length > 100 && (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(`${item.name}-${index}`)}
                            className="mt-1 sm:mt-2 text-xs font-semibold text-red-800 hover:underline dark:text-red-300"
                          >
                            {expandedCards[`${item.name}-${index}`]
                              ? t("testimonials.readLess")
                              : t("testimonials.readMore")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
          </div>
          <div className="mr-1 sm:mr-2 mt-3 sm:mt-4 flex justify-end gap-1 sm:gap-2 px-0.5 sm:px-1">
            <button
              className="relative z-40 flex h-8 sm:h-10 w-8 sm:w-10 items-center justify-center rounded-full bg-gray-100 disabled:opacity-50"
              onClick={handleScrollLeft}
              disabled={!canScrollLeft}
              type="button"
              aria-label={t("testimonials.scrollLeft")}
            >
              <IconArrowNarrowLeft className="h-4 sm:h-6 w-4 sm:w-6 text-gray-500" />
            </button>
            <button
              className="relative z-40 flex h-8 sm:h-10 w-8 sm:w-10 items-center justify-center rounded-full bg-gray-100 disabled:opacity-50"
              onClick={handleScrollRight}
              disabled={!canScrollRight}
              type="button"
              aria-label={t("testimonials.scrollRight")}
            >
              <IconArrowNarrowRight className="h-4 sm:h-6 w-4 sm:w-6 text-gray-500" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
